import { makeAutoObservable } from "mobx";
import { getEnv, getRoot } from "mobx-easy";
import { StoreState } from "../../../enum/StoreState";
import { RootEnv } from "../../setup/create-store";
import {
  IUpdateUserConfig,
  IUserAreaPosition,
  IUserConfig,
  IUserShare,
} from "./userConfigModal";
import i18n from "../../../i18n";
import RootStore from "../../root-store";
import { getMenuListMock } from "../../ui/menuView/menuItem.mock";
import { IVerifyEmailAndPhoneInUse } from "services/AdminPanelConfigService";
import { isEmbeddedMode, waitForEmbeddedToken } from "util/embedded";

export default class UserConfigStore {
  userConfig: IUserConfig = {
    name: "",
    middle_name: "",
    last_name: "",
    login: "",
    area_id: 0,
    position_id: 0,
    email: "",
    phone_code: "",
    phone_number: "",
    phone_verified: false,
  };

  userPosition: IUserAreaPosition[];

  userArea: IUserAreaPosition[];

  state: string = StoreState.DONE;

  messageError: string = "";

  users: IUserShare[];

  currentLanguage: string = i18n.language;

  timezone: string = "America/Sao_Paulo";

  constructor() {
    makeAutoObservable(this);
    // In embedded mode, wait for token before making initial API calls
    // This prevents 401 errors during store initialization
    if (isEmbeddedMode()) {
      waitForEmbeddedToken().then(() => {
        this.getUserConfigApi();
        this.getUserAreaApi();
        this.getUserPositionApi();
      });
    } else {
      this.getUserConfigApi();
      this.getUserAreaApi();
      this.getUserPositionApi();
    }
  }

  setUserConfig(value: IUserConfig) {
    this.userConfig = value;
  }

  setUserPosition(value: IUserAreaPosition[]) {
    this.userPosition = value;
  }

  setUserArea(value: IUserAreaPosition[]) {
    this.userArea = value;
  }

  setState(value: StoreState) {
    this.state = value;
  }

  setMessageError(value: string) {
    this.messageError = value;
  }

  setUsers(value: IUserShare[]) {
    this.users = value;
  }

  setTimezone(value: string) {
    this.timezone = value;
  }

  setPhoneVerified(value: boolean) {
    this.userConfig = {
      ...this.userConfig,
      phone_verified: value,
    };
  }

  reset() {
    this.state = StoreState.DONE;
    this.userConfig = {
      name: "",
      middle_name: "",
      last_name: "",
      login: "",
      area_id: 0,
      position_id: 0,
      email: "",
      phone_code: "",
      phone_number: "",
    };
    this.userArea = [];
    this.userPosition = [];
  }

  async getUserConfigApi() {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      const result: IUserConfig = await userConfigService.getUserConfig();

      this.setUserConfig(result);

      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorGetUserData"));
    }
  }

  async updateUserConfig({
    name,
    lastName,
    email,
    areaId,
    positionId,
    middleName,
    phoneCode,
    phoneNumber,
  }: IUpdateUserConfig) {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      await userConfigService.updateUserConfig({
        name,
        lastName,
        email,
        areaId,
        positionId,
        middleName,
        phoneCode,
        phoneNumber,
      });

      this.setUserConfig({
        login: this.userConfig.login,
        name: name ?? this.userConfig.name,
        last_name: lastName ?? this.userConfig.last_name,
        email: email ?? this.userConfig.email,
        area_id: areaId ?? this.userConfig.area_id,
        position_id: positionId ?? this.userConfig.position_id,
        middle_name: middleName ?? this.userConfig.middle_name,
        phone_code: phoneCode ?? this.userConfig.phone_code,
        phone_number: phoneNumber ?? this.userConfig.phone_number,
        phone_verified: this.userConfig.phone_verified,
      });
      this.setState(StoreState.SUCCESS);
      setTimeout(() => {
        this.setState(StoreState.DONE);
      }, 3000);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorUpdateUserData"));
    }
  }

  async getUserPositionApi() {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      this.setUserPosition([]);
      const result: IUserAreaPosition[] =
        await userConfigService.getUserPosition();

      this.setUserPosition(result);

      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorGetUserPositionData"));
    }
  }

  async getUserAreaApi() {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      this.setUserArea([]);
      const result: IUserAreaPosition[] = await userConfigService.getUserArea();

      this.setUserArea(result);
      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorGetUserAreaData"));
    }
  }

  async updateUserLanguage(language: string) {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      await userConfigService.updateUserLanguage(language);
      i18n.changeLanguage(language);
      this.currentLanguage = language;

      // Atualiza o atributo lang do HTML para evitar tradução automática
      document.documentElement.lang = language;

      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorUpdateUserLanguage"));
    }
  }

  async getUserLanguage() {
    const { userConfigService } = getEnv<RootEnv>();
    const {
      uiStores: { menuViewStore },
    } = getRoot<RootStore>();
    try {
      this.setState(StoreState.PEDDING);
      const preferedLanguage = await userConfigService.getUserLanguage();
      if (preferedLanguage.language) {
        i18n.changeLanguage(preferedLanguage.language);
        this.currentLanguage = preferedLanguage.language;
        // Atualiza o atributo lang do HTML para evitar tradução automática
        document.documentElement.lang = preferedLanguage.language;
      } else {
        this.updateUserLanguage(this.currentLanguage);
      }
      menuViewStore.setMenuList(getMenuListMock());

      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorUpdateUserLanguage"));
    }
  }

  async getUsersApi() {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      this.setUsers([]);
      const result = await userConfigService.getUsers();

      this.setUsers(result);
      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorGetUserAreaData"));
    }
  }

  async updateUserTimezone(timezone: string) {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      const success = await userConfigService.updateUserTimezone(timezone);

      if (success) {
        localStorage.setItem("timezone", timezone);
        this.setState(StoreState.DONE);
        return true;
      }
    } catch (error) {
      this.setState(StoreState.ERROR);
    }
  }

  async getUserTimezone() {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      const { timezone } = await userConfigService.getUserTimezone();

      if (timezone) {
        this.setTimezone(timezone);
        localStorage.setItem("timezone", timezone);
      }
      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
    }
  }

  async updateUserPhoneAndEmail(
    email: string,
    phoneCode: string,
    phoneNumber: string
  ) {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      await userConfigService.updateUserPhoneAndEmail(
        email,
        phoneCode,
        phoneNumber
      );

      this.setUserConfig({
        ...this.userConfig,
        phone_code: phoneCode,
        phone_number: phoneNumber,
        email,
      });
      this.setState(StoreState.SUCCESS);
      setTimeout(() => {
        this.setState(StoreState.DONE);
      }, 3000);
    } catch (error) {
      this.setState(StoreState.ERROR);
      this.setMessageError(i18n.t("admin.myData.errorUpdateUserData"));
    }
  }

  async updateUserConcetTermsWhatsappAlerts(
    accepted: boolean,
    text: string,
    version?: string
  ) {
    const { userConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      await userConfigService.updateUserConcetTermsWhatsappAlerts(
        accepted,
        text,
        version
      );

      this.setState(StoreState.DONE);
    } catch (error) {
      this.setState(StoreState.ERROR);
      console.error("Erro ao salvar termos de aceite:", error);
    }
  }

  async verifyEmailAndPhoneInUse({
    email,
    phone_code,
    phone_number,
  }: IVerifyEmailAndPhoneInUse) {
    const { adminPanelConfigService } = getEnv<RootEnv>();
    try {
      this.setState(StoreState.PEDDING);
      const response = await adminPanelConfigService.verifyEmailAndPhoneInUse({
        email,
        phone_code,
        phone_number,
      });

      this.setState(StoreState.DONE);

      return response;
    } catch (error) {
      this.setState(StoreState.ERROR);
      console.error("Erro ao salvar termos de aceite:", error);
    }
  }
}
